import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { ApolloQueryResult, useApolloClient, useQuery } from "@apollo/client";

import {
  MeQuery,
  OrganizationFragment,
  OrganizationQuery,
  UserFragment,
  UserRoleType,
} from "gql/graphql";
import ME_QUERY from "./networking/me";
import {
  fetchTokens,
  getUserFullName,
  isAccessTokenExpired,
} from "modules/utils/Functions";
import * as Sentry from "@sentry/nextjs";
import { NEXT_PUBLIC_API_BASE_URL } from "app/client";
import { useIndexDb } from "../index-db-provider";
import { useRouter } from "next/router";
import {
  ACCESS_TOKEN,
  AUTH_PATH,
  LOGOUT_PATH,
  SKIP_AUTH_PATHS,
} from "modules/utils/Constants";
import { useCookies } from "react-cookie";
import ORGANIZATION_QUERY from "../organization-provider/networking/organization";

interface IAuth {
  isAdmin: boolean;
  me?: UserFragment;
  organization?: OrganizationFragment;
  logout: () => Promise<void>;
  authIdentify: (input: {
    identity: string;
    type: "EMAIL" | "PHONE";
  }) => Promise<Response>;
  authPin: (input: { token: string; pin: string }) => Promise<Response>;
  isLoggedIn: boolean;
  refetchUser: () => Promise<ApolloQueryResult<MeQuery>>;
}

const AuthContext = createContext<IAuth>({
  isAdmin: false,
  me: undefined,
  organization: undefined,
  logout: async () => {},
  authIdentify: async () => {
    throw new Error("authIdentify not implemented");
  },
  authPin: async () => {
    throw new Error("authPin not implemented");
  },
  isLoggedIn: false,
  refetchUser: async () => {
    throw new Error("refetchUser not implemented");
  },
});

interface IAuthProps {
  children?: ReactNode;
}

const API_IDENTIFY_PATH = "/auth/identify";
const API_PIN_PATH = "/auth/pin";
const API_LOGOUT_PATH = "/auth/logout";

export const AuthProvider = ({ children }: IAuthProps) => {
  const client = useApolloClient();
  const { deleteAll } = useIndexDb();
  const { replace, pathname } = useRouter();

  const [accessTokenCookie] = useCookies([ACCESS_TOKEN], {
    doNotParse: true,
  });

  const accessToken = useMemo(
    () =>
      accessTokenCookie ? (accessTokenCookie.accessToken as string) : undefined,
    [accessTokenCookie],
  );

  const hasAccessToken = !!accessToken;

  const isLoggedIn = hasAccessToken;

  const { data, refetch } = useQuery<MeQuery>(ME_QUERY, {
    skip: !isLoggedIn,
  });

  const me = data?.me;

  const { data: organizationData } = useQuery<OrganizationQuery>(
    ORGANIZATION_QUERY,
    {
      skip: !isLoggedIn,
    },
  );

  const organization = organizationData?.organization;

  const logout = useCallback(async () => {
    try {
      await fetch(`${NEXT_PUBLIC_API_BASE_URL}${API_LOGOUT_PATH}`, {
        method: "GET",
        credentials: "include",
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Logout request failed:", error);
    }
    deleteAll().catch(() => {
      // best-effort local wipe; logout must proceed regardless
    });
    localStorage.clear();
    await client.clearStore();
    Sentry.setUser(null);
    replace(AUTH_PATH);
  }, [client, deleteAll, replace]);

  const isTokenRefreshing = useRef(false);

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      if (!isAccessTokenExpired() || SKIP_AUTH_PATHS.includes(pathname)) {
        return;
      }
      if (!isTokenRefreshing.current) {
        isTokenRefreshing.current = true;
        try {
          await fetchTokens();
        } catch {
          // refresh can fail transiently (offline, server blip); the next
          // interval tick retries. Without the catch the rejection is
          // unhandled AND the flag stays stuck, disabling refresh entirely.
        } finally {
          isTokenRefreshing.current = false;
        }
      }
    };

    const interval = setInterval(checkAndRefreshToken, 5 * 1000);
    return () => clearInterval(interval);
  }, [pathname]);

  useEffect(() => {
    if (!isLoggedIn && !SKIP_AUTH_PATHS.includes(pathname)) {
      replace(LOGOUT_PATH);
    }
  }, [isLoggedIn, pathname, replace]);

  useEffect(() => {
    if (!me) return;
    Sentry.setUser({
      email: me.email ?? undefined,
      id: me.id,
      username: me.callout ?? undefined,
      fullName: getUserFullName(me),
    });
  }, [me]);

  const isAdmin = useMemo(
    () => UserRoleType.Admin === me?.roleType,
    [me?.roleType],
  );

  const authIdentify = useCallback(
    (input: { identity: string; type: "EMAIL" | "PHONE" }) =>
      fetch(`${NEXT_PUBLIC_API_BASE_URL}${API_IDENTIFY_PATH}`, {
        method: "POST",
        body: JSON.stringify(input),
        headers: {
          "Content-Type": "application/json",
        },
      }),
    [],
  );

  const authPin = useCallback(
    (input: { token: string; pin: string }) =>
      fetch(`${NEXT_PUBLIC_API_BASE_URL}${API_PIN_PATH}`, {
        method: "POST",
        body: JSON.stringify(input),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }),
    [],
  );

  const value = useMemo<IAuth>(
    () => ({
      authIdentify,
      authPin,
      me,
      organization,
      isAdmin,
      logout,
      isLoggedIn,
      refetchUser: refetch,
    }),
    [
      authIdentify,
      authPin,
      me,
      organization,
      isAdmin,
      logout,
      isLoggedIn,
      refetch,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
